import { useRef, useState } from "react";
import { motion } from "framer-motion";
import {
  Building2, Cpu, Brain, Server, ShieldCheck, LineChart,
  Users, ClipboardList, Cloud, Compass, Scale, Gauge, ArrowUpRight,
} from "lucide-react";
import { Reveal } from "../lib/motion";

const FEATURED = [
  {
    icon: Building2,
    title: "Construction Program Management",
    desc: "Billion-dollar programs delivered with digital-twin precision — from blueprint to structural reality.",
    span: "md:col-span-2",
    accent: "from-nexis-blue/20",
  },
  {
    icon: Brain,
    title: "AI Enablement",
    desc: "Autonomous systems, ML platforms, and knowledge graphs engineered into enterprise operations.",
    span: "",
    accent: "from-nexis-purple/20",
  },
  {
    icon: Server,
    title: "Data Center Modernization",
    desc: "Hyperscale capacity, GPU clusters, and liquid-cooled infrastructure built for the AI era.",
    span: "",
    accent: "from-nexis-blue/20",
  },
  {
    icon: Cpu,
    title: "Enterprise Digital Transformation",
    desc: "Cloud architecture, workflow automation, and business intelligence woven into a living digital ecosystem.",
    span: "md:col-span-2",
    accent: "from-nexis-purple/20",
  },
];

const MORE = [
  { icon: ShieldCheck, label: "Critical Infrastructure" },
  { icon: LineChart, label: "Business Consulting" },
  { icon: Users, label: "Program Leadership" },
  { icon: ClipboardList, label: "PMO" },
  { icon: Cloud, label: "Cloud Transformation" },
  { icon: Compass, label: "Strategic Advisory" },
  { icon: Scale, label: "AI Governance" },
  { icon: Gauge, label: "Operational Excellence" },
];

function TiltCard({ children, className = "" }) {
  const ref = useRef(null);
  const [t, setT] = useState({ rx: 0, ry: 0 });
  const onMove = (e) => {
    const r = ref.current.getBoundingClientRect();
    const px = (e.clientX - r.left) / r.width - 0.5;
    const py = (e.clientY - r.top) / r.height - 0.5;
    setT({ rx: -py * 6, ry: px * 6 });
  };
  return (
    <motion.div
      ref={ref}
      onMouseMove={onMove}
      onMouseLeave={() => setT({ rx: 0, ry: 0 })}
      animate={{ rotateX: t.rx, rotateY: t.ry }}
      transition={{ type: "spring", stiffness: 150, damping: 18 }}
      style={{ transformStyle: "preserve-3d", perspective: 1000 }}
      className={className}
    >
      {children}
    </motion.div>
  );
}

export default function Services() {
  return (
    <section
      id="services"
      className="relative border-t border-white/5 bg-nexis-bg/80 py-28 backdrop-blur-sm md:py-40"
      data-testid="services-section"
    >
      <div className="orb orb-blue right-[5%] top-[10%] h-[360px] w-[360px]" />
      <div className="mx-auto max-w-7xl px-6 md:px-12">
        <div className="flex flex-col justify-between gap-6 md:flex-row md:items-end">
          <Reveal>
            <span className="font-mono text-xs tracking-[0.25em] text-nexis-blue">— CAPABILITIES</span>
            <h2 className="mt-5 max-w-2xl font-display text-4xl font-light tracking-tight lg:text-5xl">
              A full-stack partner for <span className="text-gradient">high-impact transformation</span>.
            </h2>
          </Reveal>
          <Reveal delay={0.15}>
            <p className="max-w-sm text-sm leading-relaxed text-white/50">
              Twelve interlocking disciplines. One senior team. We engage where strategy, infrastructure, and intelligence converge.
            </p>
          </Reveal>
        </div>

        <div className="mt-16 grid gap-4 md:grid-cols-3">
          {FEATURED.map((s, i) => (
            <Reveal key={s.title} delay={i * 0.08} className={s.span}>
              <TiltCard className="group h-full">
                <div className="glass glass-hover relative h-full overflow-hidden rounded-3xl p-8">
                  <div className={`pointer-events-none absolute -right-10 -top-10 h-40 w-40 rounded-full bg-gradient-to-br ${s.accent} to-transparent opacity-0 blur-2xl transition-opacity duration-500 group-hover:opacity-100`} />
                  <div className="flex items-center justify-between">
                    <div className="flex h-12 w-12 items-center justify-center rounded-2xl bg-white/5 text-nexis-blue ring-1 ring-white/10">
                      <s.icon className="h-5 w-5" />
                    </div>
                    <ArrowUpRight className="h-5 w-5 text-white/20 transition-all duration-300 group-hover:text-nexis-blue" />
                  </div>
                  <h3 className="mt-8 font-display text-xl font-medium tracking-tight">{s.title}</h3>
                  <p className="mt-3 text-sm leading-relaxed text-white/55">{s.desc}</p>
                </div>
              </TiltCard>
            </Reveal>
          ))}
        </div>

        <div className="mt-4 grid grid-cols-2 gap-4 md:grid-cols-4">
          {MORE.map((m, i) => (
            <Reveal key={m.label} delay={i * 0.05}>
              <div className="glass glass-hover flex items-center gap-3 rounded-2xl px-5 py-4" data-testid={`service-chip-${i}`}>
                <m.icon className="h-4 w-4 shrink-0 text-nexis-purple" />
                <span className="text-sm text-white/75">{m.label}</span>
              </div>
            </Reveal>
          ))}
        </div>
      </div>
    </section>
  );
}
